import { Button, Portal, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCopy } from '@tabler/icons-react';
import clsx from 'clsx';
import { Feature } from 'geojson';
import { useTranslation } from 'react-i18next';
import { useMap } from 'react-map-gl';
import wellknown from 'wellknown';

import { useAuth } from '@auth/data-access';
import { hasAdminAccess } from '@data-access';

import classes from './copy-wkt-button.module.css';

interface CopyWktButtonProps {
  field: Feature;
}

export function CopyWktButton({ field }: CopyWktButtonProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const map = useMap();
  const mapId = map.current?.getMap().getContainer().id;

  // Only show for admin users
  if (!hasAdminAccess(user?.user_access)) {
    return null;
  }

  const handleCopyWkt = async () => {
    try {
      // Convert GeoJSON geometry to WKT
      // Type assertion needed due to slight type differences between geojson and wellknown packages
      const wkt = wellknown.stringify(field.geometry as any);
      
      // Copy to clipboard
      await navigator.clipboard.writeText(wkt);
      
      // Show success notification
      notifications.show({
        message: t('map.copy-wkt-button.success', { defaultValue: 'WKT copié dans le presse-papiers' }),
        color: 'green',
        autoClose: 3000,
      });
    } catch (error) {
      // Show error notification
      notifications.show({
        message: t('map.copy-wkt-button.error', { defaultValue: 'Erreur lors de la copie du WKT' }),
        color: 'red',
        autoClose: 3000,
      });
    }
  };

  if (!mapId) {
    return null;
  }

  return (
    <Portal
      target={
        document &&
        (document
          .getElementById(mapId)
          ?.getElementsByClassName('mapboxgl-ctrl-top-right')[0] as HTMLElement)
      }
    >
      <Stack className={clsx(classes.container, 'mapboxgl-ctrl')}>
        <Button
          leftSection={<IconCopy size={16} />}
          onClick={handleCopyWkt}
          size="xs"
          variant="light"
        >
          {t('map.copy-wkt-button.label', { defaultValue: 'Copier le WKT' })}
        </Button>
      </Stack>
    </Portal>
  );
}

export default CopyWktButton;

